/* eslint-disable react-hooks/exhaustive-deps */
import _ from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { TimeoutWarningModal } from './TimeoutWarningModal';
import { setAutoExpiredToken, updateToken } from '../../actions/AuthActions';
import { handleErrors } from '../../actions/ErrorActions';
import { extendSessionTimeout } from '../../api/extendSessionTimeout';
import { validateToken } from '../../api/validate-token';
import { getAppSettingsByKey } from '../../selectors/AppConfigsSelectors';
import { getPrefixedKey } from '../../utils/envLocalStorage';
import { addEventListeners, removeEventListeners } from '../../utils/event-listener.utils';
import { isNilOrEmpty } from '../../utils/ramdaUtils';

let sessionWarningInterval;
const IDLE_BUFFER_TIME = 0.05;
const DATE_FORMAT = 'YYYY-MM-DD HH:mm:ss Z'; //date time format of lastUserActivityTime

export const SessionTimeoutWarning = () => {
  const warningTimeout = useSelector(getAppSettingsByKey('SmartSolve/TimeToWarnUserBeforeTimeOut'));
  const defaultAppSettingTokenLifeSpan = useSelector(
    getAppSettingsByKey('SmartSolve/TokenLifeSpan')
  );
  const prevToken = useSelector((state) => state.auth.token);

  const [isSessionModalOpen, setIsSessionModalOpen] = useState(false);
  const [sessionTimeoutDate, setSessionTimeoutDate] = useState();
  const tokenLifeSpan =
    Number(localStorage.getItem('tokenLifeSpan')) || defaultAppSettingTokenLifeSpan;

  const dispatch = useDispatch();

  const updateStorageExpireDate = useCallback((tokenLifeSpan) => {
    const endDate = new Date(new Date().getTime() + tokenLifeSpan * 60000);
    localStorage.setItem('sessionTimeoutDate', endDate);
    setSessionTimeoutDate(endDate);
  }, []);

  const updateUserActivity = useCallback(
    _.debounce(() => {
      localStorage.setItem('lastUserActivityTime', new Date());
      localStorage.getItem('isWarningPopupOpen') === 'true'
        ? localStorage.setItem('isUserActivityDetect', false)
        : localStorage.setItem('isUserActivityDetect', true);
    }, 500)
  );

  const requestToExtendSessionTimeout = async (activeUserRequestAddTime) => {
    setIsSessionModalOpen(false);
    localStorage.setItem('isWarningPopupOpen', false);
    const response = await extendSessionTimeout(
      prevToken,
      moment(activeUserRequestAddTime).format(DATE_FORMAT)
    );

    if (!response.hasError) {
      localStorage.setItem('token', response.Token);
      localStorage.setItem('tokenLifeSpan', response.TokenLifeSpan);
      dispatch(updateToken(response));

      updateStorageExpireDate(response.TokenLifeSpan);
    } else {
      dispatch(handleErrors(response.errors));
      handleLogout('auto');
    }
  };

  // Set a onStorageSessionModalUpdate event to handle changing of local storage
  // cleanup all listeners
  useEffect(() => {
    updateStorageExpireDate(tokenLifeSpan);
    const userActivityListener = (e) => updateUserActivity(e);
    addEventListeners(userActivityListener);

    const onStorageSessionModalUpdate = (e) => {
      const { key, newValue } = e;
      if (key === getPrefixedKey('sessionTimeoutDate')) {
        setSessionTimeoutDate(new Date(newValue));
      }
    };

    window.addEventListener('storage', onStorageSessionModalUpdate);

    return () => {
      removeEventListeners(userActivityListener);
      localStorage.removeItem('sessionTimeoutDate');
      window.removeEventListener('storage', onStorageSessionModalUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set interval on checking if we need to display a warning modal
  // Stop checking after opening a modal
  // After closing modal - start interval again (using isSessionModalOpen in depArr).
  useEffect(() => {
    if (!isNilOrEmpty(warningTimeout)) {
      sessionWarningInterval = setInterval(() => {
        const currentDate = new Date().getTime();
        const showWarningDate = sessionTimeoutDate - warningTimeout * 60000;

        const timeoutNumber = Number(warningTimeout);

        if (isNaN(timeoutNumber) || isNaN(tokenLifeSpan) || timeoutNumber >= tokenLifeSpan) {
          // eslint-disable-next-line no-console
          console.log('🚨 Bad Configuration found, warningTimout is greater than tokenLifeSpan.');
          clearInterval(sessionWarningInterval);
          return;
        }

        //show modal if user is inactive and his session is about to expire
        const lastUserActivityTime = new Date(
          localStorage.getItem('lastUserActivityTime')
        ).getTime();

        //Check if any user activity detect and call extendsession api before 3 seconds automatically
        const isCallExtendApiBeforeWarning = _.gte(
          currentDate,
          showWarningDate - IDLE_BUFFER_TIME * 60000
        );

        if (
          isCallExtendApiBeforeWarning &&
          localStorage.getItem('isUserActivityDetect') === 'true'
        ) {
          requestToExtendSessionTimeout(lastUserActivityTime);
          clearInterval(sessionWarningInterval);
          localStorage.setItem('isUserActivityDetect', false);
          localStorage.setItem('isWarningPopupOpen', false);
          return;
        }

        const isWarningShouldBeShown = _.gte(currentDate, showWarningDate);

        setIsSessionModalOpen(isWarningShouldBeShown);
        isWarningShouldBeShown &&
          localStorage.setItem('isWarningPopupOpen', isWarningShouldBeShown);
        isWarningShouldBeShown && clearInterval(sessionWarningInterval);
      }, 1000);
    }

    return () => {
      clearInterval(sessionWarningInterval);
    };
  }, [warningTimeout, sessionTimeoutDate]);

  const handleLogout = useCallback((logOff = '') => {
    clearInterval(sessionWarningInterval);
    localStorage.setItem('isWarningPopupOpen', false);
    if (logOff !== 'auto') {
      window.logout();
    } else {
      localStorage.setItem('autoAuthentication', false);
      localStorage.setItem('tokenLifeSpan', defaultAppSettingTokenLifeSpan);
      dispatch(setAutoExpiredToken());
      validateToken(prevToken);
    }
  });

  return (
    isSessionModalOpen && (
      <TimeoutWarningModal
        setIsSessionModalOpen={setIsSessionModalOpen}
        onRequestExtendSession={requestToExtendSessionTimeout}
        handleLogout={handleLogout}
      />
    )
  );
};
